'use client'
import * as Dialog from '@radix-ui/react-dialog'
import { useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import * as React from 'react'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Button } from '~/sotano-kit/primitives/button'
import { loadSvixPortal } from './load-portal-action'
import { invariant } from '@epic-web/invariant'
import dynamic from 'next/dynamic'

const DynamicAppPortal = dynamic(
  () => import('./svix').then((mod) => mod.SvixPortal),
  { ssr: false }
)

export const WebhooksDialog = () => {
  const { data: repoReplicacheQuery } = useRepoReplicache()
  const [shouldLoadSvix, setShouldLoadSvix] = React.useState(false)

  const enabled = shouldLoadSvix && !!repoReplicacheQuery

  const { data, isLoading } = useQuery({
    queryKey: ['svix', repoReplicacheQuery?.repoId],
    queryFn: async () => {
      invariant(repoReplicacheQuery, 'should not be enabled')
      return await loadSvixPortal({
        repoId: repoReplicacheQuery?.repoId ?? '',
        teamId: repoReplicacheQuery?.team.id,
      })
    },
    enabled,
    staleTime: Infinity,
  })

  // first sync with url
  React.useEffect(() => {
    if (window.location.search.includes('webhooks')) {
      setShouldLoadSvix(true)
    }
  }, [])

  return (
    <>
      <Dialog.Root
        open={!!data && enabled}
        onOpenChange={(v) => {
          if (v) {
            // add it to params
            window.history.pushState(
              {},
              '',
              window.location.pathname + '?webhooks=true'
            )
          } else {
            // remove it from params
            window.history.pushState({}, '', window.location.pathname)
          }
          setShouldLoadSvix((p) => !p)
        }}
      >
        <Dialog.Trigger asChild>
          <Button
            unstyled
            focusStyled
            onClick={(e) => e.stopPropagation()}
            className="text-xs font-medium hover:text-dark-dim flex items-center gap-1 cursor-default"
            iconSide="left"
            icon={<WebhookIcon />}
          >
            {isLoading && enabled ? 'Loading...' : 'Webhooks'}
          </Button>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="bg-[#000] bg-opacity-40 radix-state-open:animate-overlay-show fixed inset-0 z-overlay" />
          <Dialog.Content
            style={{
              aspectRatio: `${16}/${9}`,
            }}
            className={clsx(
              `w-max max-w-[95vw] h-[600px] max-h-[90vh] flex`,
              'radix-state-open:animate-content-show fixed top-1/2 left-1/2 z-modal translate-x-[-50%] translate-y-[-50%] rounded-2xl bg-dark-bg p-3 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none'
            )}
          >
            {enabled && data?.dashboardUrl && (
              <DynamicAppPortal url={data.dashboardUrl} />
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}

const WebhookIcon = () => {
  return (
    <svg width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.744 9.873H6.088c-.066.42-.22.819-.451 1.165-.23.347-.532.633-.881.836a2.471 2.471 0 0 1-2.292.115 2.643 2.643 0 0 1-.95-.745 2.884 2.884 0 0 1-.55-1.114 3.008 3.008 0 0 1-.041-1.26c.076-.419.239-.813.477-1.153.07-.1.174-.166.289-.183a.416.416 0 0 1 .324.09.494.494 0 0 1 .087.657c-.228.323-.35.718-.35 1.124 0 .497.185.974.513 1.325.328.352.773.55 1.237.55.465 0 .91-.198 1.238-.55.328-.351.512-.828.512-1.325 0-.125.047-.244.129-.332a.423.423 0 0 1 .309-.137h4.056a.897.897 0 0 1 .422-.397.82.82 0 0 1 .562-.04c.186.054.35.171.468.335.117.164.18.364.18.57a.982.982 0 0 1-.18.571.875.875 0 0 1-.468.335.82.82 0 0 1-.562-.04.897.897 0 0 1-.422-.397Zm-6.244.47a.83.83 0 0 0 .433-.123.906.906 0 0 0 .32-.335.988.988 0 0 0 .017-.926l1.957-3.407a.497.497 0 0 0 .053-.354.468.468 0 0 0-.197-.29 1.818 1.818 0 0 1-.574-.616 1.997 1.997 0 0 1-.13-1.688c.105-.273.267-.515.474-.708.207-.193.454-.331.72-.403.267-.072.545-.076.813-.011.268.065.518.196.73.383.211.188.379.426.488.695a.453.453 0 0 0 .242.25.41.41 0 0 0 .335-.007.456.456 0 0 0 .232-.259.5.5 0 0 0-.006-.359 2.808 2.808 0 0 0-.692-1.004 2.572 2.572 0 0 0-1.03-.584 2.455 2.455 0 0 0-1.164-.05 2.54 2.54 0 0 0-1.07.496c-.317.248-.578.57-.763.942a2.99 2.99 0 0 0-.1 2.432c.154.387.388.733.683 1.01l-1.746 3.04H3.5a.846.846 0 0 0-.618.275.973.973 0 0 0-.257.663c0 .248.093.487.257.663a.847.847 0 0 0 .618.274Zm7-3.75c-.337 0-.671.069-.984.204L7.77 3.757a.996.996 0 0 0 .095-.592.961.961 0 0 0-.26-.531.828.828 0 0 0-1.054-.128.923.923 0 0 0-.362.455 1 1 0 0 0-.032.599c.05.198.16.372.313.497a.838.838 0 0 0 .53.191h.025L8.98 7.656a.44.44 0 0 0 .272.21.413.413 0 0 0 .33-.057 1.643 1.643 0 0 1 1.553-.151c.252.104.478.27.66.485.182.215.315.472.389.75.074.28.086.573.036.858-.05.285-.16.554-.324.785a1.785 1.785 0 0 1-.617.548c-.242.129-.509.196-.78.196a.423.423 0 0 0-.309.137.487.487 0 0 0-.128.331c0 .125.046.244.128.332a.424.424 0 0 0 .31.137 2.54 2.54 0 0 0 1.856-.824 2.918 2.918 0 0 0 .769-1.988c0-.746-.277-1.462-.77-1.99a2.54 2.54 0 0 0-1.855-.823Z"
        fill="currentColor"
      />
    </svg>
  )
}
